@if (error) {
  <div class="error-banner">
    <div class="error-banner__content">{{ error.message }}</div>
  </div>
}

<div class="main_body">
  <div class="dashboard">
    <!-- Page Header -->
    <app-page-header [title]="i18n.title.dashboard" icon="dashboard" />

    <div class="dashboard__content">
    @if (isLoading()) {
      <!-- Skeleton Loading -->
      <div class="dashboard-stat-cards dashboard-stat-cards--5col">
        @for (_ of [1,2,3,4,5]; track $index) {
          <div class="dashboard-stat-card">
            <div class="dashboard-stat-card__icon-box skel" style="background: none;"></div>
            <div class="dashboard-stat-card__content">
              <div class="skel" style="width: 60%; height: 12px;"></div>
              <div class="skel" style="width: 45%; height: 24px;"></div>
              <div class="skel" style="width: 80%; height: 10px;"></div>
            </div>
          </div>
        }
      </div>
      <div class="dashboard-main">
        <div class="dashboard-card">
          <div class="dashboard-card__header">
            <div class="skel" style="width: 160px; height: 18px;"></div>
          </div>
          <div class="dashboard-card__body">
            <div class="skel" style="width: 100%; height: 260px; border-radius: var(--radius-md);"></div>
          </div>
        </div>
        <div class="dashboard-card">
          <div class="dashboard-card__header">
            <div class="skel" style="width: 160px; height: 18px;"></div>
          </div>
          <div class="dashboard-card__body">
            <div class="skel" style="width: 100%; height: 260px; border-radius: var(--radius-md);"></div>
          </div>
        </div>
      </div>
    } @else {
      <!-- Row 1: 5-column Stat Cards -->
      <div class="dashboard-stat-cards dashboard-stat-cards--5col">
        <!-- Section Count -->
        <div class="dashboard-stat-card">
          <div class="dashboard-stat-card__icon-box dashboard-stat-card__icon-box--primary">
            <mat-icon>domain</mat-icon>
          </div>
          <div class="dashboard-stat-card__content">
            <span class="dashboard-stat-card__label">{{ i18n.label.sectionCount }}</span>
            <div class="dashboard-stat-card__value">
              <span class="dashboard-stat-card__number">{{ numOfSections | number }}</span>
              <span class="dashboard-stat-card__unit" i18n="@@common.unit.items">件</span>
            </div>
            @if (sectionTrend !== 0) {
              <div class="dashboard-stat-card__trend"
                   [class.dashboard-stat-card__trend--positive]="sectionTrend > 0"
                   [class.dashboard-stat-card__trend--negative]="sectionTrend < 0">
                <mat-icon>{{ sectionTrend > 0 ? 'trending_up' : 'trending_down' }}</mat-icon>
                <span>{{ sectionTrend > 0 ? '+' : '' }}{{ sectionTrend }} {{ i18n.label.monthOverMonth }}</span>
              </div>
            } @else {
              <div class="dashboard-stat-card__trend dashboard-stat-card__trend--neutral">
                <mat-icon>trending_flat</mat-icon>
                <span>±0 {{ i18n.label.monthOverMonth }}</span>
              </div>
            }
          </div>
        </div>

        <!-- Staff Count (purple) -->
        <div class="dashboard-stat-card">
          <div class="dashboard-stat-card__icon-box dashboard-stat-card__icon-box--purple">
            <mat-icon>people</mat-icon>
          </div>
          <div class="dashboard-stat-card__content">
            <span class="dashboard-stat-card__label">{{ i18n.label.totalStaff }}</span>
            <div class="dashboard-stat-card__value">
              <span class="dashboard-stat-card__number">{{ numOfStaffs | number }}</span>
              <span class="dashboard-stat-card__unit" i18n="@@common.unit.people">名</span>
            </div>
            @if (staffTrend !== 0) {
              <div class="dashboard-stat-card__trend"
                   [class.dashboard-stat-card__trend--positive]="staffTrend > 0"
                   [class.dashboard-stat-card__trend--negative]="staffTrend < 0">
                <mat-icon>{{ staffTrend > 0 ? 'trending_up' : 'trending_down' }}</mat-icon>
                <span>{{ staffTrend > 0 ? '+' : '' }}{{ staffTrend }} {{ i18n.label.monthOverMonth }}</span>
              </div>
            } @else {
              <div class="dashboard-stat-card__trend dashboard-stat-card__trend--neutral">
                <mat-icon>trending_flat</mat-icon>
                <span>±0 {{ i18n.label.monthOverMonth }}</span>
              </div>
            }
          </div>
        </div>

        <!-- Contributor Count (warning/orange) -->
        <div class="dashboard-stat-card">
          <div class="dashboard-stat-card__icon-box dashboard-stat-card__icon-box--warning">
            <mat-icon>diversity_3</mat-icon>
          </div>
          <div class="dashboard-stat-card__content">
            <span class="dashboard-stat-card__label">{{ i18n.label.totalContributors }}</span>
            <div class="dashboard-stat-card__value">
              <span class="dashboard-stat-card__number">{{ numOfContributors | number }}</span>
              <span class="dashboard-stat-card__unit" i18n="@@common.unit.people">名</span>
            </div>
            @if (contributorTrend !== 0) {
              <div class="dashboard-stat-card__trend"
                   [class.dashboard-stat-card__trend--positive]="contributorTrend > 0"
                   [class.dashboard-stat-card__trend--negative]="contributorTrend < 0">
                <mat-icon>{{ contributorTrend > 0 ? 'trending_up' : 'trending_down' }}</mat-icon>
                <span>{{ contributorTrend > 0 ? '+' : '' }}{{ contributorTrend }} {{ i18n.label.monthOverMonth }}</span>
              </div>
            } @else {
              <div class="dashboard-stat-card__trend dashboard-stat-card__trend--neutral">
                <mat-icon>trending_flat</mat-icon>
                <span>±0 {{ i18n.label.monthOverMonth }}</span>
              </div>
            }
          </div>
        </div>

        <!-- User Count (success/green) -->
        <div class="dashboard-stat-card">
          <div class="dashboard-stat-card__icon-box dashboard-stat-card__icon-box--success">
            <mat-icon>person</mat-icon>
          </div>
          <div class="dashboard-stat-card__content">
            <span class="dashboard-stat-card__label">{{ i18n.label.totalUsers }}</span>
            <div class="dashboard-stat-card__value">
              <span class="dashboard-stat-card__number">{{ numOfUsers | number }}</span>
              <span class="dashboard-stat-card__unit" i18n="@@common.unit.people">名</span>
            </div>
            @if (userTrend !== 0) {
              <div class="dashboard-stat-card__trend"
                   [class.dashboard-stat-card__trend--positive]="userTrend > 0"
                   [class.dashboard-stat-card__trend--negative]="userTrend < 0">
                <mat-icon>{{ userTrend > 0 ? 'trending_up' : 'trending_down' }}</mat-icon>
                <span>{{ userTrend > 0 ? '+' : '' }}{{ userTrend }} {{ i18n.label.monthOverMonth }}</span>
              </div>
            } @else {
              <div class="dashboard-stat-card__trend dashboard-stat-card__trend--neutral">
                <mat-icon>trending_flat</mat-icon>
                <span>±0 {{ i18n.label.monthOverMonth }}</span>
              </div>
            }
          </div>
        </div>

        <!-- Data Usage (info/blue) -->
        <div class="dashboard-stat-card">
          <div class="dashboard-stat-card__icon-box dashboard-stat-card__icon-box--info">
            <mat-icon>storage</mat-icon>
          </div>
          <div class="dashboard-stat-card__content">
            <span class="dashboard-stat-card__label">{{ i18n.label.currentUsage }}</span>
            <div class="dashboard-stat-card__value">
              <span class="dashboard-stat-card__number">{{ currentUsageGB | number:'1.1-1' }}</span>
              <span class="dashboard-stat-card__unit">GB</span>
            </div>
            <div class="dashboard-stat-card__progress">
              <div class="dashboard-stat-card__progress-fill"
                   [class.dashboard-stat-card__progress-fill--warning]="usagePercentage > 80"
                   [class.dashboard-stat-card__progress-fill--danger]="usagePercentage > 95"
                   [style.width.%]="usagePercentage"></div>
            </div>
            <span class="dashboard-stat-card__sub-info"
                  [class.dashboard-stat-card__sub-info--warning]="usagePercentage > 80">
              {{ i18n.label.remaining }} {{ remainingGB | number:'1.1-1' }} GB / {{ quotaLimitGB }} GB
            </span>
          </div>
        </div>
      </div>

      <!-- Row 2: Data Usage Trend + Notices -->
      <div class="dashboard-main">
        <!-- Chart A: Monthly Data Usage (Bar) -->
        <div class="dashboard-card">
          <div class="dashboard-card__header">
            <h2 class="dashboard-card__title">{{ i18n.title.dataUsage }}（{{ currentYear }}年）</h2>
            <mat-icon class="dashboard-card__icon">bar_chart</mat-icon>
          </div>
          <div class="dashboard-card__body">
            <div class="dashboard-chart-area">
              <apx-chart
                [series]="barChartSeries"
                [chart]="barChartOptions"
                [xaxis]="barChartXAxis"
                [yaxis]="barChartYAxis"
                [dataLabels]="barChartDataLabels"
                [plotOptions]="barChartPlotOptions"
                [stroke]="barChartStroke"
                [fill]="barChartFill"
                [grid]="barChartGrid"
                [tooltip]="barChartTooltip"
                [colors]="barChartColors"
              ></apx-chart>
            </div>
          </div>
        </div>

        <!-- Notices Card -->
        <div class="dashboard-card">
          <div class="dashboard-card__header">
            <h2 class="dashboard-card__title">{{ i18n.title.importantNotices }}</h2>
            <mat-icon class="dashboard-card__icon">info</mat-icon>
          </div>
          <div class="dashboard-card__body">
            <div class="dashboard-notice-list">
              @for (notice of notices; track notice.date) {
                <div class="dashboard-notice-item">
                  <span class="dashboard-notice-item__date">{{ notice.date }}</span>
                  <p class="dashboard-notice-item__text">
                    @if (notice.link) {
                      <a [routerLink]="notice.link">{{ notice.text }}</a>
                    } @else {
                      {{ notice.text }}
                    }
                  </p>
                </div>
              }
            </div>
          </div>
        </div>
      </div>

      <!-- Row 3: User Trend + Section Ranking -->
      <div class="dashboard-main">
        <!-- Chart C: Section User Trend (Stacked Area) -->
        <div class="dashboard-card">
          <div class="dashboard-card__header">
            <h2 class="dashboard-card__title">{{ i18n.chartTitle.userTrend }}（{{ areaChartPeriodLabel }}）</h2>
            <mat-icon class="dashboard-card__icon">show_chart</mat-icon>
          </div>
          <div class="dashboard-card__body">
            <div class="dashboard-chart-area">
              <apx-chart
                [series]="areaChartSeries"
                [chart]="areaChartOptions"
                [xaxis]="areaChartXAxis"
                [yaxis]="areaChartYAxis"
                [dataLabels]="areaChartDataLabels"
                [stroke]="areaChartStroke"
                [fill]="areaChartFill"
                [grid]="areaChartGrid"
                [legend]="areaChartLegend"
                [tooltip]="areaChartTooltip"
              ></apx-chart>
            </div>
          </div>
        </div>

        <!-- Chart B: Section Data Usage Ranking (Horizontal Bar, top 5) -->
        <div class="dashboard-card dashboard-card--ranking">
          <div class="dashboard-card__header">
            <h2 class="dashboard-card__title">{{ i18n.chartTitle.sectionRanking }}</h2>
            <mat-icon class="dashboard-card__icon">leaderboard</mat-icon>
          </div>
          <div class="dashboard-card__body">
            <div class="dashboard-chart-area">
              <apx-chart
                [series]="rankChartSeries"
                [chart]="rankChartOptions"
                [plotOptions]="rankChartPlotOptions"
                [xaxis]="rankChartXAxis"
                [yaxis]="rankChartYAxis"
                [dataLabels]="rankChartDataLabels"
                [tooltip]="rankChartTooltip"
                [legend]="rankChartLegend"
                [colors]="rankChartColors"
              ></apx-chart>
            </div>
          </div>
        </div>
      </div>
    }
    </div>
  </div>
</div>
